import { Box, Link as MuiLink, Typography } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import type { SupportTicketAttachment } from "../../types.js";
import { formatBytes, isImage } from "./attachmentUtils.js";

/**
 * Ticket attachments — an image grid + a non-image download list, with a
 * file-picker upload affordance + inline error. Extracted from
 * `TicketDetailPage.tsx`.
 */
export function AttachmentsPanel({
  attachments,
  onUpload,
  isUploading,
  error,
}: {
  attachments: SupportTicketAttachment[];
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  error: string | null;
}): JSX.Element {
  const images = attachments.filter((a) => isImage(a.content_type));
  const others = attachments.filter((a) => !isImage(a.content_type));

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Box
        component="label"
        sx={{
          display: "inline-flex",
          width: "fit-content",
          cursor: "pointer",
          alignItems: "center",
          gap: 1,
          borderRadius: 999,
          border: 1,
          borderColor: "divider",
          bgcolor: "surfaceSoft",
          px: 1.5,
          py: 0.75,
          fontSize: 12,
          color: "text.primary",
          "&:hover": { borderColor: "text.disabled" },
        }}
      >
        <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ height: 12, width: 12 }}>
          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </Box>
        {isUploading ? "Uploading…" : "Attach file(s)"}
        <Box
          component="input"
          type="file"
          multiple
          disabled={isUploading}
          sx={{ display: "none" }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) onUpload(files);
            e.target.value = "";
          }}
        />
      </Box>

      {error && (
        <Typography
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: (t) => alpha(t.palette.error.main, 0.4),
            bgcolor: (t) => alpha(t.palette.error.main, 0.05),
            px: 1.5,
            py: 1,
            fontSize: 12,
            color: "error.main",
          }}
        >
          {error}
        </Typography>
      )}

      {attachments.length === 0 && (
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
          No attachments yet.
        </Typography>
      )}

      {images.length > 0 && (
        <Box component="ul" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1, listStyle: "none", m: 0, p: 0 }}>
          {images.map((a) => (
            <Box component="li" key={a.id}>
              <Box
                component="a"
                href={a.download_url ?? a.preview_url ?? "#"}
                target="_blank"
                rel="noreferrer"
                title={a.filename}
                sx={{
                  display: "block",
                  overflow: "hidden",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "surfaceSoft",
                  transition: "border-color 150ms",
                  textDecoration: "none",
                  "&:hover": { borderColor: "text.disabled" },
                }}
              >
                <Box sx={{ aspectRatio: "1 / 1", width: "100%", overflow: "hidden", bgcolor: "action.hover" }}>
                  {a.preview_url ? (
                    <Box
                      component="img"
                      src={a.preview_url}
                      alt={a.filename}
                      loading="lazy"
                      sx={{ height: "100%", width: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <FileIcon sx={{ height: 40, width: 40, opacity: 0.4 }} />
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 1, py: 0.75, fontSize: 12 }}>
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "text.primary" }}>
                    {a.filename}
                  </Box>
                  <Box component="span" sx={{ flexShrink: 0, color: "text.disabled" }}>
                    {formatBytes(a.byte_size)}
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {others.length > 0 && (
        <Box component="ul" sx={{ display: "grid", gap: 0.75, listStyle: "none", m: 0, p: 0 }}>
          {others.map((a) => (
            <Box
              component="li"
              key={a.id}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "surfaceSoft", px: 1.5, py: 1, fontSize: 14 }}
            >
              <FileIcon sx={{ height: 16, width: 16, color: "text.disabled" }} />
              {a.download_url || a.preview_url ? (
                <MuiLink
                  href={a.download_url ?? a.preview_url!}
                  target="_blank"
                  rel="noreferrer"
                  color="text.primary"
                  underline="hover"
                  sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}
                >
                  {a.filename}
                </MuiLink>
              ) : (
                <Box component="span" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>{a.filename}</Box>
              )}
              <Box component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
                {formatBytes(a.byte_size)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function FileIcon({ sx }: { sx?: SxProps<Theme> }): JSX.Element {
  return (
    <Box component="svg" viewBox="0 0 16 16" aria-hidden {...(sx ? { sx } : {})}>
      <path
        d="M9 1.5H4a1.5 1.5 0 00-1.5 1.5v10A1.5 1.5 0 004 14.5h8a1.5 1.5 0 001.5-1.5V6L9 1.5zM9 1.5V6h4.5"
        stroke="currentColor"
        strokeWidth="1.25"
        fill="none"
        strokeLinejoin="round"
      />
    </Box>
  );
}
